<template>
  <div>
    <v-sheet id="main" class="overflow-y-auto mt-xs-2">
      <!-- Banner Section -->
      <section v-show="headline" id="banner-section" class="pt-10 mt-10 pb-5">
        <v-row no-gutters align="center" justify="space-around" class="mx-auto" style="width: 80%;">
          <v-col cols="12" sm="8">
            <div
              class="neutral--text text-h6 text-lg-h4 font-weight-bold text-center"
              style="overflow-wrap: break-word;"
            >
              <BannerSection @headlineLoaded="setHeadline" />
            </div>
          </v-col>

          <v-col cols="12" sm="auto" class="d-flex justify-center">
            <v-btn class="neutral white--text" x-large :to="{ name: 'Banner' }">
              Read More
            </v-btn>
          </v-col>
        </v-row>
      </section>

      <!-- First Section -->
      <section id="home-first-section">
        <v-row no-gutters align="center" class="px-4 pt-8 px-md-8 pt-md-12">
          <v-col cols="12" md="4" class="first-section-bordered order-1 pl-md-16 pr-md-6">
            <h1 class="first-section-title black--text text-bold mb-0">
              Science is <br class="d-none d-md-block" />
              better <br />
              together.
            </h1>
          </v-col>

          <v-col cols="12" md="4" class="order-3 order-md-2 mt-7 mt-md-0 px-md-4">
            <video autoplay muted loop playsinline class="first-section-video">
              <source src="@/assets/homePageVideo.mp4" type="video/mp4" />
            </video>
          </v-col>

          <v-col
            cols="12"
            md="4"
            class="first-section-bordered order-2 order-md-3 mt-4 mt-md-0 pl-md-16 pr-md-6"
          >
            <p class="first-section-copy text-medium mb-0">
              Your first-class ticket to research services, equipment and lab facilities.
            </p>
          </v-col>
        </v-row>
      </section>

      <!-- Second Section -->
      <section id="home-second-section">
        <v-btn
          :to="servicesCatalogue"
          rounded
          color="#9747ff"
          height="50"
          class="browse-facilities-button text-none white--text text-medium"
        >
          Browse Facilities
        </v-btn>

        <v-card
          flat
          dark
          class="community-card rounded-xl white--text pa-6 pa-md-16"
          style="background-color: #101318 !important; border-color: #101318 !important;"
        >
          <div class="community-card-content">
            <h2 class="community-title text-bold mb-0">
              Be a part of our<br />
              growing community!
            </h2>

            <div class="community-stats">
              <v-btn
                v-if="statistics.institutions > 0"
                depressed
                class="community-stat-card text-none"
                @click="redirectToHostingCatalogue"
              >
                <span class="community-stat-value text-medium">
                  <counter
                    :key="statistics.institutions"
                    :end-count="statistics.institutions"
                    :duration="500"
                  />
                </span>
                <span class="community-stat-label">Hosting Institutions</span>
              </v-btn>

              <v-btn
                v-if="statistics.providers > 0"
                depressed
                class="community-stat-card text-none"
                @click="redirectToProviderCatalogue"
              >
                <span class="community-stat-value text-medium">
                  <counter :end-count="statistics.providers" :duration="500" />
                </span>
                <span class="community-stat-label">Providers</span>
              </v-btn>

              <v-btn
                v-if="statistics.resources > 0"
                depressed
                class="community-stat-card text-none"
                @click="redirectToServicesCatalogue"
              >
                <span class="community-stat-value text-medium">
                  <counter :end-count="statistics.resources" :duration="4000" />
                </span>
                <span class="community-stat-label">Services</span>
              </v-btn>

              <v-btn
                v-if="statistics.equipment > 0"
                depressed
                class="community-stat-card text-none"
                @click="redirectToEquipmentCatalogue"
              >
                <span class="community-stat-value text-medium">
                  <counter :end-count="statistics.equipment" :duration="5000" />
                </span>
                <span class="community-stat-label">Equipment</span>
              </v-btn>

              <v-btn
                v-if="statistics.intellectualProperties > 0"
                depressed
                class="community-stat-card text-none"
                @click="redirectToPatentsCatalogue"
              >
                <span class="community-stat-value text-medium">
                  <counter :end-count="statistics.intellectualProperties" :duration="500" />
                </span>
                <span class="community-stat-label">Intellectual Properties</span>
              </v-btn>

              <v-chip v-if="users > 0" class="community-stat-card">
                <span class="community-stat-value text-medium">
                  <counter :end-count="users" :duration="5000" />
                </span>
                <span class="community-stat-label">Registered Users</span>
              </v-chip>
            </div>
          </div>
        </v-card>
      </section>

      <!-- Third Section -->
      <section id="home-third-section" class="px-4 px-md-8 pb-8 pb-md-16">
        <v-row>
          <v-col cols="12" md="6" class="d-flex py-2">
            <v-card
              elevation="3"
              height="100%"
              class="third-section-card rounded-xl pa-5 pa-md-12 d-flex flex-column justify-space-between"
              style="background-color: #faf6ff !important; border-color: #faf6ff !important;"
            >
              <div>
                <p class="third-section-kicker text-medium mb-1">Grow your reach</p>
                <h2 class="third-section-title text-bold mb-6">FACILITIES MANAGER</h2>
                <div class="third-section-copy text-medium">
                  <p class="mb-2">Showcase &amp; commercialize your infrastructure</p>
                  <p class="mb-2">Broaden your licensing portfolio</p>
                  <p class="mb-2">Join the UAE's top specialized lab map</p>
                  <p class="mb-0">Diversify your audience</p>
                </div>
              </div>

              <v-btn
                :to="exploreRoute"
                block
                rounded
                dark
                color="#101318"
                height="48"
                :ripple="false"
                class="third-section-button text-none white--text text-medium mt-6"
              >
                Explore
                <v-icon right small color="white">mdi-arrow-right</v-icon>
              </v-btn>
            </v-card>
          </v-col>

          <v-col cols="12" md="6" class="d-flex py-2">
            <v-card
              elevation="3"
              height="100%"
              class="third-section-card rounded-xl pa-5 pa-md-12 d-flex flex-column justify-space-between"
              style="background-color: #faf6ff !important; border-color: #faf6ff !important;"
            >
              <div>
                <p class="third-section-kicker text-medium mb-1">Accelerate discovery</p>
                <h2 class="third-section-title text-bold mb-6">RESEARCHER</h2>
                <div class="third-section-copy text-medium">
                  <p class="mb-2">Find the tools you need</p>
                  <p class="mb-2">Connect directly with lab experts</p>
                  <p class="mb-2">Accelerate your research project</p>
                  <p class="mb-0">Access a growing national catalogue</p>
                </div>
              </div>

              <v-btn
                :to="exploreRoute"
                block
                rounded
                dark
                color="#101318"
                height="48"
                :ripple="false"
                class="third-section-button text-none white--text text-medium mt-6"
              >
                Explore
                <v-icon right small color="white">mdi-arrow-right</v-icon>
              </v-btn>
            </v-card>
          </v-col>
        </v-row>
      </section>

      <!-- Fourth Section -->
      <section id="home-fourth-section">
        <v-card
          flat
          tile
          dark
          class="fourth-section-card px-6 py-10 px-md-16 py-md-16"
          style="background-color: #101318 !important; border: 0 !important;"
        >
          <v-row no-gutters>
            <v-col cols="12" md="6" class="fourth-section-panel pb-10 pb-md-0 pr-md-12">
              <v-avatar color="#ead8ff" size="40">
                <v-icon color="#101318" size="24">mdi-eye-outline</v-icon>
              </v-avatar>

              <p class="fourth-section-label text-bold mt-4 mb-2">Vision</p>
              <h2 class="fourth-section-title white--text text-bold mb-6">
                Connected UAE’s scientific ecosystem
              </h2>

              <div class="fourth-section-copy">
                <p>A unified, accessible catalogue of research facilities across the country</p>
                <p>
                  Connect academia, industry, and government to accelerate scientific discovery and
                  real-world impact
                </p>
                <p>Maximize utilization of facilities, services, and intellectual property</p>
                <p class="mb-0">
                  Establish UAE as a globally connected hub for research, innovation, and advanced
                  technology
                </p>
              </div>
            </v-col>

            <v-col
              cols="12"
              md="6"
              class="fourth-section-panel fourth-section-mission pt-10 pt-md-0 pl-md-12"
            >
              <v-avatar color="#ead8ff" size="40">
                <v-icon color="#101318" size="24">mdi-bullseye-arrow</v-icon>
              </v-avatar>

              <p class="fourth-section-label text-bold mt-4 mb-2">Mission</p>
              <h2 class="fourth-section-title white--text text-bold mb-6">
                Strengthen the UAE’s research landscape
              </h2>

              <div class="fourth-section-copy">
                <p>
                  Our mission is to strengthen the UAE’s research landscape by making it easier to
                  discover, access, and engage with world-class research capabilities.
                </p>
                <p class="mb-0">
                  We offer providers a centralized platform to showcase and commercialize their
                  research infrastructure and expertise free of charge.
                </p>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </section>

      <!-- Fifth Section -->
      <section id="home-fifth-section" class="px-4 py-10 px-md-8 py-md-16 my-md-14">
        <v-card
          flat
          outlined
          class="fifth-section-card rounded-lg"
          style="background-color: #f8f9fa !important; border-color: #d9dde3 !important;"
        >
          <v-row no-gutters>
            <v-col
              v-for="feature in fifthSectionFeatures"
              :key="feature.title"
              cols="12"
              md="4"
              class="fifth-section-feature d-flex flex-column align-center justify-center text-center px-5 py-8 py-md-12"
            >
              <v-avatar color="white" size="44" class="fifth-section-icon elevation-1">
                <v-icon color="#9747ff" size="24">{{ feature.icon }}</v-icon>
              </v-avatar>

              <h2 class="fifth-section-title text-bold mt-4 mb-2">
                {{ feature.title }}
              </h2>
              <p class="fifth-section-copy text-medium mb-0">
                {{ feature.description }}
              </p>
            </v-col>
          </v-row>
        </v-card>
      </section>

      <!-- Sixth Section -->
      <section id="home-sixth-section" class="px-4 pb-10 px-md-8 pb-md-16">
        <div class="text-center mb-8 mb-md-12">
          <h2 class="catalogue-title black--text text-bold mb-3">Catalogue</h2>
          <p class="catalogue-intro text-medium mx-auto mb-0">
            Explore a rich catalogue of resources in a diverse spectrum of scientific domains and
            order from our marketplace with just a few clicks.
          </p>
        </div>

        <v-row>
          <v-col
            v-for="card in catalogueCards"
            :key="card.title"
            cols="12"
            md="4"
            class="d-flex py-2"
          >
            <v-card
              :to="card.to"
              flat
              outlined
              :ripple="false"
              class="catalogue-card rounded-xl d-flex flex-column"
            >
              <div class="catalogue-card-header text-center px-5 py-5">
                <h3 class="catalogue-card-title black--text text-bold mb-1">
                  {{ card.title }}
                </h3>
                <p class="catalogue-card-copy text-medium mb-0">
                  {{ card.description }}
                </p>
              </div>
              <v-img :src="card.image" aspect-ratio="1.08" class="catalogue-card-image" />
            </v-card>
          </v-col>
        </v-row>
      </section>

      <!-- Seventh Section -->
      <section id="home-seventh-section" class="pb-10 pb-md-16">
        <h2 class="partners-title text-center text-bold mb-6 mb-md-10">Our Partners</h2>

        <v-carousel
          v-if="institutions.length"
          :height="$vuetify.breakpoint.smAndDown ? 112 : 150"
          hide-delimiters
          :show-arrows="false"
          class="partners-carousel"
        >
          <v-carousel-item eager>
            <div class="partners-viewport d-flex align-center fill-height">
              <div class="partners-track">
                <div v-for="copy in 2" :key="copy" class="partners-group" :aria-hidden="copy === 2">
                  <v-card
                    v-for="(partner, partnerIndex) in institutions"
                    :key="copy + '-' + (partner.id || partner.name || partnerIndex)"
                    :href="partner.url || undefined"
                    :target="partner.url ? '_blank' : undefined"
                    flat
                    outlined
                    :ripple="false"
                    class="partner-card rounded-xl d-flex align-center justify-center pa-3"
                  >
                    <v-img
                      :src="partner.logoUrl"
                      :alt="partner.name"
                      contain
                      max-height="72"
                      max-width="190"
                    />
                  </v-card>
                </div>
              </div>
            </div>
          </v-carousel-item>
        </v-carousel>
      </section>

      <!-- subscribe Section -->
      <section id="subscribe" class="px-4 px-md-8 pb-10 pb-md-16">
        <v-card
          flat
          dark
          color="black"
          class="subscribe-section-card rounded-xl text-center px-5 py-10 px-md-12 py-md-16"
          style="background-color: #101318 !important; border-color: #faf6ff !important;"
        >
          <h2 class="subscribe-section-title white--text text-bold mb-3">Stay Connected</h2>
          <p class="subscribe-section-copy text-medium mx-auto mb-0">
            Subscribe for the Latest Updates and Insights!
          </p>

          <v-form
            v-if="!subscribed"
            class="subscribe-section-form mx-auto mt-6 mt-md-8"
            @submit.prevent="subscribe"
          >
            <v-row no-gutters align="start">
              <v-col cols="12" md="8" class="pr-md-2">
                <v-text-field
                  v-model="email"
                  :rules="emailRules()"
                  placeholder="Your email Address"
                  type="email"
                  outlined
                  background-color="black"
                  hide-details="auto"
                  data-cy="email"
                  class="subscribe-section-input"
                />
              </v-col>
              <v-col cols="12" md="4" class="mt-3 mt-md-0 pl-md-2">
                <v-btn
                  type="submit"
                  color="white"
                  block
                  x-large
                  class="subscribe-section-button rounded-lg black--text text-bold"
                  style="border-radius: 60px !important;"
                >
                  Subscribe
                </v-btn>
              </v-col>
            </v-row>
          </v-form>

          <v-alert
            v-else
            dense
            text
            type="success"
            class="subscribe-section-success mx-auto mt-6 mb-0"
          >
            Welcome on board
          </v-alert>
        </v-card>
      </section>
    </v-sheet>
  </div>
</template>

<script>
import util from '@/mixins/util.js';
import BannerSection from '@/components/landing-page/BannerSection.vue';
import Counter from '@/components/shared/Counter';
import scientificsJs from '@/configs/scientifics.json';
import categoriesJs from '@/configs/categories.json';
import institutionsImage from '@/assets/img/landing-page/institutions.webp';
import providersImage from '@/assets/img/landing-page/providers.webp';
import resourcesImage from '@/assets/img/landing-page/resources.webp';

export default {
  name: 'HomePageNew',
  components: { Counter, BannerSection },
  mixins: [util],
  data: function() {
    return {
      drawer: false,
      group: null,
      email: '',
      subscribed: false,
      statistics: {
        resources: null,
        providers: null,
        scientificDomains: null,
        categories: null,
        equipment: null,
        institutions: 0,
        intellectualProperties: null,
      },
      users: null,
      items: [1, 2, 3],
      toggle_tab: 'scientific',
      scientifics: scientificsJs,
      categories: categoriesJs,
      institutions: [],
      headline: '',
      fifthSectionFeatures: [
        {
          title: 'Search',
          description: 'UAE-accessible research resources',
          icon: 'mdi-magnify',
        },
        {
          title: 'Engage',
          description: 'Institutions and cutting-edge resources, all in one place',
          icon: 'mdi-cart-outline',
        },
        {
          title: 'Innovate',
          description: 'Shape the UAE’s future in science and innovation',
          icon: 'mdi-forum-outline',
        },
      ],
    };
  },
  computed: {
    exploreRoute() {
      return this.$store.getters.roles ? { name: 'Dashboard' } : { name: 'SignUp' };
    },
    catalogueCards() {
      return [
        {
          title: 'Hosting Institution',
          description: 'Explore the affiliated hosting institutions.',
          image: institutionsImage,
          to: this.hostingInstitutionsCatalogue,
        },
        {
          title: 'Providers',
          description: 'Visit the catalogue of our providers.',
          image: providersImage,
          to: this.providerCatalogue,
        },
        {
          title: 'Resources',
          description: 'Browse the catalogue of various resources and place your order.',
          image: resourcesImage,
          to: this.servicesCatalogue,
        },
      ];
    },
    providerCatalogue() {
      const roles = this.$store.getters.roles;
      const marketplaceProvidersViewAll = true;
      const selectedCatalogue = 'Browse the Catalogue of Providers';

      let routePath = '';
      if (roles != null) {
        routePath = '/marketplace/providers';
      } else {
        routePath = '/public-catalogue-marketplace/providers';
      }

      return {
        path: routePath,
        query: { selectedCatalogue, marketplaceProvidersViewAll },
      };
    },
    servicesCatalogue() {
      const roles = this.$store.getters.roles;
      const marketplaceServicesViewAll = true;
      const selectedCatalogue = 'Browse the Catalogue of Services';

      let routePath = '';
      if (roles != null) {
        routePath = '/marketplace/services';
      } else {
        routePath = '/public-catalogue-marketplace/services';
      }

      return {
        path: routePath,
        query: { selectedCatalogue, marketplaceServicesViewAll },
      };
    },
    equipmentCatalogue() {
      const roles = this.$store.getters.roles;
      const marketplaceEquipmentViewAll = true;
      const selectedCatalogue = 'Browse the Catalogue of Equipment';

      let routePath = '';
      if (roles != null) {
        routePath = '/marketplace/equipment';
      } else {
        routePath = '/public-catalogue-marketplace/equipment';
      }

      return {
        path: routePath,
        query: { selectedCatalogue, marketplaceEquipmentViewAll },
      };
    },
    hostingInstitutionsCatalogue() {
      const roles = this.$store.getters.roles;
      const selectedCatalogue = 'Browse the Catalogue of Hosting Institutions';

      let routePath = '';
      if (roles != null) {
        routePath = '/marketplace/hosting-institutions';
      } else {
        routePath = '/public-catalogue-marketplace/hosting-institutions';
      }

      return {
        path: routePath,
        query: { selectedCatalogue },
      };
    },
  },
  mounted() {
    this.axios.get('/api/v1/hosting-institutions/all').then(res => {
      this.statistics.institutions = res.data.length;
      this.institutions = res.data;
    });

    this.axios.get('/api/v1/landing/statistics').then(res => {
      Object.assign(this.statistics, res.data);
    });

    this.axios.get('/api/v1/landing/users').then(res => {
      this.users = res.data;
    });

    this.axios.get('/api/v1/equipment').then(res => {
      this.statistics.equipment = res.data.total;
    });

    this.axios.get('/api/v1/patent').then(res => {
      this.statistics.intellectualProperties = res.data.total;
    });

    window.addEventListener('load', () => {
      if (window.location.hash) {
        setTimeout(() => {
          const el = document.querySelector(window.location.hash);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 500);
      }
    });
  },

  methods: {
    subscribe: function() {
      let request = {
        email: this.email,
      };
      this.axios
        .post('/api/v1/subscriptions', request)
        .then(() => (this.subscribed = !this.subscribed));
    },
    setHeadline(isLoaded) {
      this.headline = isLoaded;
    },
    exploreMore() {
      if (this.$store.getters.roles) {
        let routePath = '';
        const marketplaceServicesViewAll = true;
        const selectedCatalogue = 'Browse the Catalogue of Services';
        routePath = '/marketplace/services';
        this.$router.push({
          path: routePath,
          query: { selectedCatalogue, marketplaceServicesViewAll },
        });
      } else {
        let routePath = '';
        const marketplaceServicesViewAll = true;
        const selectedCatalogue = 'Browse the Catalogue of Services';
        routePath = '/public-catalogue-marketplace/services';
        this.$router.push({
          path: routePath,
          query: { selectedCatalogue, marketplaceServicesViewAll },
        });
      }
    },
    redirectToProviderCatalogue() {
      const roles = this.$store.getters.roles;
      const marketplaceProvidersViewAll = true;
      const selectedCatalogue = 'Browse the Catalogue of Providers';
      if (roles != null) {
        let routePath = '';
        routePath = '/marketplace/providers';
        this.$router.push({
          path: routePath,
          query: { selectedCatalogue, marketplaceProvidersViewAll },
        });
      } else {
        let routePath = '';
        routePath = '/public-catalogue-marketplace/providers';
        this.$router.push({
          path: routePath,
          query: { selectedCatalogue, marketplaceProvidersViewAll },
        });
        this.isMarketPlaceMenuOpen = false;
      }
    },
    redirectToHostingCatalogue() {
      const roles = this.$store.getters.roles;
      const selectedCatalogue = 'Browse the Catalogue of Hosting Institutions';
      if (roles != null) {
        let routePath = '';
        routePath = '/marketplace/hosting-institutions';
        this.$router.push({
          path: routePath,
          query: { selectedCatalogue },
        });
      } else {
        let routePath = '';
        routePath = '/public-catalogue-marketplace/hosting-institutions';
        this.$router.push({
          path: routePath,
          query: { selectedCatalogue },
        });
        this.isMarketPlaceMenuOpen = false;
      }
    },
    redirectToServicesCatalogue() {
      const roles = this.$store.getters.roles;
      const marketplaceServicesViewAll = true;
      const selectedCatalogue = 'Browse the Catalogue of Services';
      if (roles != null) {
        let routePath = '';
        routePath = '/marketplace/services';
        this.$router.push({
          path: routePath,
          query: { selectedCatalogue, marketplaceServicesViewAll },
        });
      } else {
        let routePath = '';
        routePath = '/public-catalogue-marketplace/services';
        this.$router.push({
          path: routePath,
          query: { selectedCatalogue, marketplaceServicesViewAll },
        });
        this.isMarketPlaceMenuOpen = false;
      }
    },
    redirectToEquipmentCatalogue() {
      const roles = this.$store.getters.roles;
      const marketplaceEquipmentViewAll = true;
      const selectedCatalogue = 'Browse the Catalogue of Equipment';

      if (roles != null) {
        let routePath = '';
        routePath = '/marketplace/equipment';
        this.$router.push({
          path: routePath,
          query: { selectedCatalogue, marketplaceEquipmentViewAll },
        });
      } else {
        let routePath = '';
        routePath = '/public-catalogue-marketplace/equipment';
        this.$router.push({
          path: routePath,
          query: { selectedCatalogue, marketplaceEquipmentViewAll },
        });
        this.isMarketPlaceMenuOpen = false;
      }
    },
    redirectToPatentsCatalogue() {
      const roles = this.$store.getters.roles;
      const selectedCatalogue = 'Browse the Catalogue of Patents';
      const marketplacePatentsViewAll = true;
      if (roles != null) {
        let routePath = '';
        routePath = '/marketplace/patents';
        this.$router.push({
          path: routePath,
          query: { selectedCatalogue, marketplacePatentsViewAll },
        });
      } else {
        let routePath = '';
        routePath = '/public-catalogue-marketplace/patents';
        this.$router.push({
          path: routePath,
          query: { selectedCatalogue, marketplacePatentsViewAll },
        });
        this.isMarketPlaceMenuOpen = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped src="../../styles/home-page-new.scss"></style>
